import { PermissionsEnum } from "@/auth/interfaces";
import { useColumnsAgendamientos } from "@/erp/operaciones/agendamiento/shared/hooks";
import {
  EstadosAgendamientoEnumChoice,
  EstadosContratoChoiceEnum,
  ModulosEnumChoice,
  TABLE_CONSTANTS_COLUMS,
} from "@/shared";
import { useFetchAgendamientos } from "@/shared/actions/erp";
import { GeneralTable, TableTabsScene } from "@/shared/components/ui";
import ModalMotivoRechazo from "@/shared/components/ui/ModalMotivoRechazo/ModalMotivoRechazo";
import { useModalMotivoRechazo } from "@/shared/components/ui/ModalMotivoRechazo/useModalMotivoRechazo";
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from "@/shared/hooks";
import { useTableFilter } from "@/shared/hooks/useTableFilter";
import { Agendamiento, IDataBitacoraRespById } from "@/shared/interfaces";
import { hasPermission } from "@/shared/utils";
import { useDiccionarioMotivo } from "@/store/erp/diccionario_motivo/diccionario_motivo.store";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import { Grid, IconButton, Tooltip } from "@mui/material";
import useEstadoInstalacionSofnet from "@/shared/hooks/useEstadoInstalacionSofnet";

export type AgendamientoRecodianadoTableProps = {
  enableActions?: boolean;
};

const AgendamientoRecodianadoTable: React.FC<
  AgendamientoRecodianadoTableProps
> = ({ enableActions = true }) => {
  useCheckPermissions(PermissionsEnum.api_view_agendamiento);

  const {
    emitGetVentaById,
    ///openCreaBitacora,
    setOpenCreaBitacora,
    setDetalle,
    setFaseVenta,
    //detalle,
    setOpen,
    socket,
  } = useDiccionarioMotivo();

  const useHookModalMotivoRechazo = useModalMotivoRechazo({
    modulo: ModulosEnumChoice.AGENDAMIENTO,
  });

  const {
    handleValidacion
  } = useEstadoInstalacionSofnet();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();


  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: agendamientosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchAgendamientos({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      identificacion: searchTerm,
      ...filterObject,
      filterByState: false,
      contrato_estado_contrato_exclude: EstadosContratoChoiceEnum.ACTIVO,
      estado_agendamiento_in: `${EstadosAgendamientoEnumChoice.RECORDINADO},${EstadosAgendamientoEnumChoice.PENDIENTE_RECORDINACION}`,
    },
  });

  ///* columns
  const { agendamientoColumnaVentas } = useColumnsAgendamientos();

  return (
    <>
      <TableTabsScene
        globalFilter={globalFilter}
        onChangeFilter={onChangeFilter}
        searchInputText="por identificación"
        //onChangeRangeDate={onChangeRangeDate}
        cleanFilter={cleanFilter}
      >
        <GeneralTable<Agendamiento>
          columns={agendamientoColumnaVentas}
          data={agendamientosPagingRes?.data?.data || []}
          isLoading={isLoading}
          isRefetching={isRefetching}
          // // filters - server side
          enableManualFiltering={true}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          // // search
          enableGlobalFilter={false}
          // // pagination
          pagination={pagination}
          onPaging={setPagination}
          rowCount={agendamientosPagingRes?.data?.count}
          // // actions ========
          enableColumnActions={hasPermission(
            PermissionsEnum.api_change_agendamiento
          )}
          // crud
          positionActionsColumn="first"
          canEdit={false}
          //enableEditing={false}
          showCustomActionBtnSpace={true}
          enableEditing={enableActions}
          actionsColumnSize={TABLE_CONSTANTS_COLUMS.COLUMN_WIDTH_SMALL}
          customActionBtnSpace={(row) => {
            return (
              <Grid container>
                <Grid item>
                  <Tooltip title="Crear Bitácora">
                    <IconButton
                      onClick={() => {
                        if (!socket) return;

                        const idPreagendamiento =
                          row.preagendamiento_data?.id_preagendamiento;

                        if (!idPreagendamiento) return;

                        socket.emit(
                          "getVentaById",
                          {
                            numero_referencia_venta: idPreagendamiento,
                          },
                          (data: IDataBitacoraRespById) => {
                            if (data.code == 200) {
                              emitGetVentaById(idPreagendamiento);
                              setOpen(true);
                              return;
                            }

                            const details = {
                              numero_referencia_venta: idPreagendamiento,
                              identificacion_cliente:
                                row.prospecto_data?.identificacion,
                              nombre_cliente: row.prospecto_data?.razon_social,
                              canal_venta:
                                row.prospecto_data?.canal_venta_create,
                              vendedor: row.prospecto_data?.user_create,
                              fecha_aceptacion:
                                row.preventa_data?.fecha_aceptacion,
                            };
                            setDetalle(details);
                            setFaseVenta(ModulosEnumChoice.RECORDINADO);
                            setOpenCreaBitacora(true);
                            return;
                          }
                        );
                      }}
                    >
                      <ForumRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>

                <Grid item>
                  <Tooltip title={"Cancelar"} placement="top">
                    <IconButton
                      onClick={async () => {

                        const aux = await handleValidacion(row?.preagendamiento_data?.id_instalacion_externa);

                        if (aux) return;

                        useHookModalMotivoRechazo.setDialogContentText(
                          `Estás a punto de cancelar un Agendamiento del cliente ${row.prospecto_data?.razon_social} con identificación ${row.prospecto_data?.identificacion}. ¿Estás seguro? Este cambio es irreversible.`
                        );
                        useHookModalMotivoRechazo.setModalCancelar(true);
                        useHookModalMotivoRechazo.reset({
                          id: row.id_agendamiento,
                          identificacion: row.prospecto_data?.identificacion,
                          razon_social: row.prospecto_data?.razon_social,
                          observaciones: row.observaciones,
                          id_preagendamiento:
                            row.preagendamiento_data?.id_preagendamiento,
                        });
                      }}
                    >
                      <HighlightOffIcon color="error" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            );
          }}
        />
      </TableTabsScene>

      {/*Modal para cancelar*/}
      <ModalMotivoRechazo {...useHookModalMotivoRechazo} />
    </>
  );
};

export default AgendamientoRecodianadoTable;
